/**
 * Legal Disclaimer Page (v3.0)
 *
 * Full legal disclaimer and terms of service.
 * Accessible via footer link.
 */

import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'

export default function LegalPage() {
  const { t } = useTranslation(['legal', 'common'])

  return (
    <div className="min-h-screen py-12 bg-background-primary">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link
          to="/app"
          className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('legal:backToHome')}
        </Link>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">{t('legal:title')}</h1>
          <p className="text-gray-400">{t('legal:lastUpdated')}</p>
        </div>

        {/* Content */}
        <div className="space-y-8 text-gray-300">
          {/* Section 1: General Information Only */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.general.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:sections.general.content')}
            </p>
          </section>

          {/* Section 2: Nature of the Platform */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.nature.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:sections.nature.content')}
            </p>
          </section>

          {/* Section 3: Risk Acknowledgment */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.risk.title')}</h2>
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6 mb-4">
              <p className="text-yellow-400 font-semibold mb-2">{t('legal:sections.risk.warningTitle')}</p>
              <p className="leading-relaxed">
                {t('legal:sections.risk.warningText')}
              </p>
            </div>
            <p className="leading-relaxed mb-4">{t('legal:sections.risk.intro')}</p>
            <ul className="list-disc list-inside space-y-3 ml-4">
              <li>{t('legal:sections.risk.protocolRisk')}</li>
              <li>{t('legal:sections.risk.marketVolatility')}</li>
              <li>{t('legal:sections.risk.regulatoryRisk')}</li>
              <li>{t('legal:sections.risk.liquidityRisk')}</li>
            </ul>
          </section>

          {/* Section 4: Yield and Reward Estimates */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.yield.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:sections.yield.content')}
            </p>
          </section>

          {/* Section 5: Principal Protection and RWA */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.principal.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:sections.principal.content')}
            </p>
          </section>

          {/* Section 6: No Warranties */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.warranties.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:sections.warranties.content')}
            </p>
          </section>

          {/* Section 7: User Responsibility */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.userResponsibility.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:sections.userResponsibility.content')}
            </p>
          </section>

          {/* Section 8: Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.liability.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:sections.liability.content')}
            </p>
          </section>

          {/* Section 9: Additional Information */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{t('legal:sections.additional.title')}</h2>
            <p className="leading-relaxed mb-3">
              {t('legal:sections.additional.para1')}
            </p>
            <p className="leading-relaxed mb-3">
              {t('legal:sections.additional.para2')}
            </p>
            <p className="leading-relaxed">
              {t('legal:sections.additional.para3')}
            </p>
          </section>

          {/* Acknowledgment */}
          <section className="bg-gold-500/10 border border-gold-500/30 rounded-lg p-6 mt-12">
            <h2 className="text-xl font-bold text-gold-400 mb-4">{t('legal:acknowledgment.title')}</h2>
            <p className="leading-relaxed">
              {t('legal:acknowledgment.content')}
            </p>
          </section>
        </div>

        {/* Back to Home Button */}
        <div className="mt-12 text-center">
          <Link
            to="/app"
            className="btn-gold inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold"
          >
            <ArrowLeft className="h-5 w-5" />
            {t('legal:returnToHome')}
          </Link>
        </div>
      </div>
    </div>
  )
}
